// a page that allow guest member to select plan by location

import { useRouter } from '@abyss/web/hooks/useRouter';
import { styled } from '@abyss/web/tools/styled';
import { Box } from '@abyss/web/ui/Box';
import { Divider } from '@abyss/web/ui/Divider';
import { Text } from '@abyss/web/ui/Text';
import { find } from 'lodash';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { GuestState } from '../..//store/useGuestStore/guestStore';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { getGeoLocationFromStorage } from '../../common/PSXHeader/SearchBar/utils';
import { getFeatureFlag } from '../../common/Utils';
import { useSaveEducationalContentStatus } from '../../hooks/useEducationalContent';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { useGuestStore } from '../../store/useGuestStore';
import { FirstTimeUserBanner } from './common/FirstTimeUserBanner';
import { GuestMultiplePlan } from './GuestMultiplePlan';
import { NullLocationMessageContainer } from './GuestPlanSelection.style';
import {
  GuestLocation,
  isSinglePlan,
} from './selectLocation/GuestLocation.utils';
import { GuestLocationSelect } from './selectLocation/GuestLocationSelect';

const PlanSelectionPageBody = ({
  backgroundColor = '$bgHeaderColor',
  height = '100vh',
  children,
}) => (
  <Box
    color={backgroundColor}
    css={{
      minHeight: height,
      margin: 0,
      padding: 0,
    }}
  >
    {children}
  </Box>
);
const LocationSelectionWrapper = styled('section', {
  backgroundColor: '$white',
  margin: 0,
  padding: '30px 20px',
  paddingBottom: '35px',
  width: '100%',
});

const PlanSelectionDivider = () => (
  <Box css={{ borderBottom: 'solid 1px $gray3', padding: 0 }}>
    <Divider color="$bgHeaderColor" height={9} margin={0} />
  </Box>
);
export const GuestPlanSelection = () => {
  const [selectedLocation, setSelectedLocation] = useState<
    GuestLocation | undefined
  >(undefined);
  const psxLocation = getGeoLocationFromStorage();
  const savedLocation = sessionStorage.getItem('guestLocation');
  // Initialize selectedLocation from sessionStorage or geo location on mount
  useEffect(() => {
    if (psxLocation && savedLocation) {
      let parsedSavedLocation;
      try {
        parsedSavedLocation = JSON.parse(savedLocation);
      } catch {
        parsedSavedLocation = null;
      }
      if (parsedSavedLocation) {
        const isSameLocation =
          psxLocation.latitude === parsedSavedLocation.latitude &&
          psxLocation.longitude === parsedSavedLocation.longitude;

        const location = {
          ...psxLocation,
          zipcode: psxLocation.zipCode,
          label: psxLocation.name,
        };

        setSelectedLocation(isSameLocation ? parsedSavedLocation : location);
        return;
      }
    }
    if (savedLocation) {
      setSelectedLocation(JSON.parse(savedLocation));
    }
  }, [psxLocation, savedLocation]);

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const { getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();

  const lob = searchParams?.get('planSelectionLob') || '';

  const coverageType = searchParams?.get('coverageType');

  const { setChooseType } = useGuestStore(
    useShallow((state) => ({
      setChooseType: (state as GuestState).setChooseType,
    }))
  );

  const featureFlags = useLagoon('feature-flags')();

  const isFirstTimeUserBannerEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.GUEST_FIRST_TIME_USER_BANNERS_ENABLED
  );

  const lagoonUIMessage = useLagoon('ui-messaging')();
  const nullLocationMessage = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.NULL_LOCATION_MESSAGE,
  })?.message;
  const chooseMedicaidPlanText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_MEDICAID_PLAN,
  })?.message;
  const chooseAPlanText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_A_PLAN,
  })?.message;
  const chooseMedicarePlanText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_MEDICARE_PLAN,
  })?.message;
  const chooseALocationText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION.CHOOSE_A_LOCATION,
  })?.message;
  const bannerContentText = find(lagoonUIMessage, {
    key: ConstantsLagoon.GUEST_PLAN_SELECTION
      .PLAN_SELECTION_FIRST_TIME_USER_BANNER_TEXT,
  })?.message;

  const educationalContentStore = useStore(
    StoreKeys.EDUCATIONAL_CONTENT_STATUS
  );

  const setEducationalContentStore = useStore(
    StoreKeys.SET_EDUCATIONAL_CONTENT_STATUS
  );

  const [shouldShowFirstTimeUserBanner, setShouldShowFirstTimeUserBanner] =
    useState(false);

  const [saveEducationalContent, setEducationalContentStatus] =
    useSaveEducationalContentStatus({});

  const handleOnClose = () => {
    setShouldShowFirstTimeUserBanner(false);
    setEducationalContentStore(
      saveEducationalContent.data.saveEducationalContentStatus
    );
  };

  const isSinglePlanResult = isSinglePlan(lob, coverageType);

  useEffect(() => {
    if (selectedLocation) {
      if (!sessionStorage.getItem('guestLocation')) {
        setShouldShowFirstTimeUserBanner(
          educationalContentStore?.hasViewedGuestPlanBanner === false &&
            isFirstTimeUserBannerEnabled
        );
        if (educationalContentStore?.hasViewedGuestPlanBanner === false) {
          setEducationalContentStatus({
            variables: {
              ...educationalContentStore,
              hasViewedContent:
                educationalContentStore?.hasViewedContent ?? true,
              hasViewedGuestPlanBanner: true,
            },
          });
        }
      }
      sessionStorage.setItem('guestLocation', JSON.stringify(selectedLocation));
    } else {
      sessionStorage.removeItem('guestLocation');
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (isSinglePlanResult && !selectedLocation) {
      setChooseType(chooseALocationText);
    } else if (lob === 'CS' && coverageType === 'M') {
      setChooseType(chooseMedicaidPlanText);
    } else if (lob === 'MR') {
      setChooseType(chooseMedicarePlanText);
    } else {
      setChooseType(chooseAPlanText);
    }
  }, [selectedLocation]);

  return (
    <PlanSelectionPageBody
      backgroundColor={
        selectedLocation || isSinglePlanResult ? '$white' : '$bgHeaderColor'
      }
      height={selectedLocation || isSinglePlanResult ? 'auto' : '100vh'}
    >
      {shouldShowFirstTimeUserBanner && (
        <FirstTimeUserBanner
          content={bannerContentText}
          onClose={handleOnClose}
          showLearnMoreButton={false}
        />
      )}
      <LocationSelectionWrapper>
        <GuestLocationSelect
          isDataLoaded={isDataLoaded}
          selectedLocation={selectedLocation}
          setIsDataLoaded={setIsDataLoaded}
          setSelectedLocation={setSelectedLocation}
        />
      </LocationSelectionWrapper>

      {(selectedLocation || !isSinglePlanResult) && (
        <div>
          <Divider color="$gray3" height="1px" margin="0" />
          {selectedLocation ? (
            <div>
              <PlanSelectionDivider />
              <GuestMultiplePlan selectedLocation={selectedLocation} />
            </div>
          ) : (
            <NullLocationMessageContainer justify="center">
              <Text>{nullLocationMessage}</Text>
            </NullLocationMessageContainer>
          )}
        </div>
      )}
    </PlanSelectionPageBody>
  );
};
